import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvent } from '../context/EventContext';
import { useUserProfile } from '../hooks/useUserProfile';
import Button from '../components/Button';
import AddVendorModal from '../components/AddVendorModal';
import NotificationModal from '../components/NotificationModal';
import TaskDetailModal from '../components/TaskDetailModal';

const Vendors = () => {
  const navigate = useNavigate();
  const { eventData, vendorCount, loading: eventLoading } = useEvent();
  const [vendors, setVendors] = useState([]);
  const [vendorTasks, setVendorTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showDetailView, setShowDetailView] = useState(false);
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);

  const loadVendors = async () => {
      if (!eventData?.eventId) return;
      
      try {
        // Get vendor associations for this event
        const vendorsResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/vendors`);
        if (vendorsResponse.ok) {
          const vendorAssociations = await vendorsResponse.json();
          
          // Get vendor details and tasks for each vendor
          const vendorDetails = [];
          const allTasks = {};
          
          for (const association of vendorAssociations) {
            const vendorId = association.vendorId;
            if (!vendorId || vendorId === 'undefined') continue;
            
            // Get vendor details
            try {
            const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}`);
            if (vendorResponse.ok) {
              const vendorData = await vendorResponse.json();
              if (!vendorData) continue;
              // Standardize DynamoDB format
              const vendor = {};
              for (const [key, value] of Object.entries(vendorData)) {
                if (value?.S !== undefined) vendor[key] = value.S;
                else if (value?.N !== undefined) vendor[key] = parseInt(value.N);
                else if (value?.BOOL !== undefined) vendor[key] = value.BOOL;
                else vendor[key] = value;
              }
              vendorDetails.push({
                ...vendor,
                isPrimary: association.isPrimary === 'true'
              });
            }
            } catch (e) {
              console.log('Failed to load vendor:', vendorId);
            }
            
            // Get tasks for this vendor
            const tasksResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/tasks`);
            if (tasksResponse.ok) {
              const allEventTasks = await tasksResponse.json();
              // Standardize task data
              const standardizedTasks = allEventTasks.map(task => {
                const standardized = {};
                for (const [key, value] of Object.entries(task)) {
                  if (value?.S !== undefined) standardized[key] = value.S;
                  else if (value?.N !== undefined) standardized[key] = parseInt(value.N);
                  else if (value?.BOOL !== undefined) standardized[key] = value.BOOL;
                  else standardized[key] = value;
                }
                return standardized;
              });
              const vendorSpecificTasks = standardizedTasks.filter(task => task.vendorId === vendorId);
              allTasks[vendorId] = vendorSpecificTasks;
            }
          }
          
          setVendors(vendorDetails);
          setVendorTasks(allTasks);
        }
      } catch (error) {
        console.error('Error loading vendors:', error);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadVendors();
  }, [eventData]);

  if (eventLoading || profileLoading || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading vendors...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '32px 24px 140px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '40px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '42px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Vendors
        </h1>
        
        {/* Stats Card positioned at bottom of green area */}
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div className="stats-card" style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M3 21h18"></path>
              <path d="M5 21V7l8-4v18"></path>
              <path d="M19 21V9.5l-6-2.5"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {vendorCount} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>vendors</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {vendors.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
            <h3 style={{ fontSize: '24px', fontFamily: 'Georgia, serif', marginBottom: '12px' }}>No Vendors Yet</h3>
            <p>Add vendors to start managing your wedding services.</p>
          </div>
        ) : (
          vendors.map((vendor) => {
            const tasks = vendorTasks[vendor.id] || [];
            return (
              <div key={vendor.id} style={{ marginBottom: '48px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <h2 
                    style={{ 
                      fontSize: '32px', 
                      fontWeight: '400',
                      margin: 0,
                      color: '#1F2937',
                      fontFamily: 'Georgia, serif',
                      cursor: 'pointer',
                      transition: 'color 0.2s'
                    }}
                    onClick={() => navigate(`/vendors/${vendor.id}`)}
                    onMouseOver={(e) => e.target.style.color = '#6B9080'}
                    onMouseOut={(e) => e.target.style.color = '#1F2937'}
                  >
                    {vendor.name} {vendor.vendorType && <span style={{ fontSize: '14px', color: '#6B7280' }}>({vendor.vendorType.charAt(0).toUpperCase() + vendor.vendorType.slice(1)})</span>}
                  </h2>
                  {vendor.status === 'pending' && (
                    <span style={{
                      padding: '6px 12px',
                      backgroundColor: '#FEF3C7',
                      color: '#92400E',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      Pending user sign up
                    </span>
                  )}
                </div>
                
                {tasks.length === 0 ? (
                  <div style={{ 
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center',
                    color: '#6B7280'
                  }}>
                    No tasks assigned yet
                  </div>
                ) : (
                  tasks.map((task) => (
                    <div 
                      key={task.id} 
                      style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        border: '1px solid #E5E7EB',
                        marginBottom: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        setSelectedTask(task);
                        setShowDetailView(true);
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: '#E8F4E8',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: '#6B9080'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                          <path d="M9 11l3 3l8-8"></path>
                          <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"></path>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '8px' }}>
                          {task.name}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {task.documentUrl && (
                            <Button 
                              variant="outline" 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = task.documentUrl;
                                link.download = task.name || 'document';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              Open Document
                            </Button>
                          )}
                        </div>
                      </div>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '16px'
                      }}>{task.status === 'completed' ? '✓' : ''}</div>
                    </div>
                  ))
                )}
              </div>
            );
          })
        )}

        {/* Add Vendor Button */}
        <div>
          <Button variant="primary" size="large" onClick={() => setShowAddVendorModal(true)}>
            Add Vendor
          </Button>
        </div>
      </div>
      
      <AddVendorModal
        isOpen={showAddVendorModal}
        onClose={() => setShowAddVendorModal(false)}
        eventId={eventData?.eventId}
        onVendorAdded={() => {
          setLoading(true);
          loadVendors();
        }}
      />
      
      <NotificationModal
        isOpen={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />
      
      <TaskDetailModal
        task={showDetailView ? selectedTask : null}
        onClose={() => {
          setShowDetailView(false);
          setSelectedTask(null);
        }}
        eventId={eventData?.eventId}
        currentUser={currentUser}
        onStatusUpdate={() => {
          setLoading(true);
          loadVendors();
        }}
      />
    </div>
  );
};

export default Vendors;